import classNames from 'classnames'
import React, { ReactNode } from 'react'

import Data from 'data'
import Store, { StoreState } from 'store'

import {
  faChevronDown,
  faCog,
  faCopy,
  faFileImport,
  faPlusSquare,
  faTimes,
  faTimesCircle
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { SmallIcon } from 'components/Icon'
import { LibraryNode, createCategories, itemCategories } from 'library'
import { WithItemTooltip } from 'tooltip'
import Resizable from 'ui/resizable'
import { searchRegex } from 'utils'

import ItemLibrary, { LibraryClickHandler } from 'components/ItemLibrary'
import {
  encodeTooltipOptions,
  itemBaseIcon,
  itemCreate,
  itemGetBaseColor,
  itemGetName,
  itemParseText,
  itemTranslationDomain
} from 'logic/item'
import Dialog from 'ui/dialog'

import { EmbedLevel } from '@/common'
import { statsExtend } from '@/logic/mods'
import Session from '@/store/session'
import { Item, ItemTooltipOptions, StatType } from '@/types/item'
import { notNull } from '@/utils/validation'
import { CopyNotify, copyText } from 'components/copy'
import { ItemDisplay } from 'components/embeds/Paperdoll'
import Trans from 'data/trans'
import Tooltip from 'ui/tooltip'
import { ErrorPopup } from 'ui/utils/Overlay'
import { OverlayContext } from 'ui/utils/OverlayManager'
import { useImportId } from '../importTracker'
import ItemEditor from './ItemEditor'

function slotCategories(slot: string): LibraryNode {
  const types = new Set<string>()
  for (const [type, info] of Object.entries(Data.item_classes)) {
    if (info.slots && info.slots.includes(slot)) types.add(type)
  }
  function createCategory(src: LibraryNode): LibraryNode | undefined {
    if (src.types && !src.types.some((type) => types.has(type))) return
    if (!src.children) return src
    const children = src.children.map((ch) => createCategory(ch)).filter(notNull)
    if (!children.length) return
    if (children.length === 1 && src !== createCategories) {
      return children[0]
    }
    return {
      name: src.name,
      children,
      filter: (item) => types.has(item.item_class) && src.filter(item)
    }
  }
  return (
    createCategory(createCategories) ?? {
      name: createCategories.name,
      filter: () => null
    }
  )
}

export type ItemFilter = {
  filter: (item: Item) => any
  categories: () => LibraryNode
}
export const slotFilter = (slot: string): ItemFilter => ({
  filter(item) {
    const base = Data.items[item.base]
    if (!base) return false
    return Data.item_classes[base.item_class]?.slots?.includes(slot)
  },
  categories: () => slotCategories(slot)
})

type CustomNode = {
  name: string
  children?: CustomNode[]
  items?: number[]
}

function libraryCategories(items: StoreState['items']): CustomNode {
  const types: Record<string, number[]> = {}
  for (const [id, item] of Object.entries(items)) {
    const base = item && Data.items[item.base]
    if (!base) continue
    types[base.item_class] = types[base.item_class] || []
    types[base.item_class].push(Number(id))
  }
  function createCategory(src: LibraryNode): CustomNode | undefined {
    const children = src.children?.map((ch) => createCategory(ch)).filter(notNull)
    if (children?.length) {
      if (children.length === 1 && src !== itemCategories) {
        return {
          ...children[0],
          name: src.name
        }
      } else {
        return { name: src.name, children }
      }
    } else if (src.types) {
      const list: number[] = []
      for (const type of src.types) {
        if (types[type]) list.push(...types[type].filter((id) => src.filter(Data.items[items[id]!.base])))
      }
      if (!list.length) return
      list.sort((a, b) => itemGetName(items[a]!).localeCompare(itemGetName(items[b]!)))
      return { name: src.name, items: list }
    }
  }
  return createCategory(itemCategories) || { name: itemCategories.name }
}

function LibraryItem({
  id,
  active,
  setActive
}: {
  id: number
  active: number | undefined
  setActive: (id: number | undefined) => void
}) {
  const item = Store.useItem(id)!
  const icon = itemBaseIcon(
    (item.unique && Data.uniques[item.unique]) || Data.items[item.base],
    item.map_tier,
    item.variant
  )
  const color = itemGetBaseColor(Data.items[item.base]) || item.rarity || 'normal'
  return (
    <li className={classNames({ 'poe-active': active === id })}>
      <div className={`poe-library-item poe-item-${color}`}>
        <WithItemTooltip item={item}>
          <span onClick={() => setActive(id)}>
            {!!icon && <SmallIcon icon={icon} />} {itemGetName(item)}
          </span>
        </WithItemTooltip>
      </div>
    </li>
  )
}

function LibraryEmpty({
  active,
  setActive
}: {
  active: number | undefined
  setActive: (id: number | undefined) => void
}) {
  return (
    <li className={classNames({ 'poe-active': active == null })}>
      <div className="poe-item-empty">
        <span onClick={() => setActive(undefined)}>
          <FontAwesomeIcon icon={faTimes} /> Empty Slot
        </span>
      </div>
    </li>
  )
}

function BaseSelector({ filter, onSelect }: { filter?: ItemFilter; onSelect: (id: string | undefined) => void }) {
  const onClick = React.useCallback<LibraryClickHandler>((e, id) => onSelect(id), [onSelect])
  const categories = React.useMemo(() => filter?.categories() ?? createCategories, [filter])

  return (
    <Dialog className="poe-ItemSelector" modal title="Select Item" onClose={() => onSelect(undefined)}>
      <div className="poe-ItemSelector-content">
        <ItemLibrary root={categories} onClick={onClick} uniques />
      </div>
    </Dialog>
  )
}

function LibraryCreate({ filter, setActive }: { filter?: ItemFilter; setActive: (id: number | undefined) => void }) {
  const [creating, setCreating] = React.useState(false)
  const store = Store.useStore()
  const dispatch = Store.useDispatch()
  const onSelectItem = React.useCallback(
    (name: string | undefined) => {
      setCreating(false)
      if (name) {
        const item = itemCreate(name)
        if (!item) return
        const items = store.getState().items
        let id = 1
        while (items[id]) id += 1
        dispatch.setItem(id, item)
        setActive(id)
      }
    },
    [store, dispatch, setActive]
  )
  return (
    <li>
      <div className="poe-item-create">
        <span onClick={() => setCreating(true)}>
          <FontAwesomeIcon icon={faPlusSquare} /> Create...
        </span>
      </div>
      {!!creating && <BaseSelector filter={filter} onSelect={onSelectItem} />}
    </li>
  )
}

function ImportDialog({ onAccept, onClose }: { onAccept: (text: string) => void; onClose: () => void }) {
  const ref = React.useRef<HTMLTextAreaElement>(null)
  return (
    <Dialog className="poe-ImportDialog" title={`Import item`} modal onClose={onClose}>
      <Dialog.Content>
        <textarea ref={ref} />
      </Dialog.Content>
      <Dialog.Footer>
        <button onClick={() => onAccept(ref.current!.value)}>{`Import`}</button>
      </Dialog.Footer>
    </Dialog>
  )
}

function LibraryImport({ filter, setActive }: { filter?: ItemFilter; setActive: (id: number | undefined) => void }) {
  const [importing, setImporting] = React.useState(false)
  const store = Store.useStore()
  const dispatch = Store.useDispatch()
  const overlays = React.useContext(OverlayContext)
  const onImport = React.useCallback(
    (text: string) => {
      const item = itemParseText(text)
      if (!item) {
        overlays.async(<ErrorPopup message="Failed to parse item" />)
        return
      }
      if (filter && !filter.filter(item)) {
        overlays.async(<ErrorPopup message="Item does not fit in this slot" />)
        return
      }
      setImporting(false)
      const items = store.getState().items
      let id = 1
      while (items[id]) id += 1
      dispatch.setItem(id, item)
      setActive(id)
    },
    [store, dispatch, setActive, filter, overlays]
  )
  return (
    <li>
      <div className="poe-item-create">
        <span onClick={() => setImporting(true)}>
          <FontAwesomeIcon icon={faFileImport} /> Import...
        </span>
      </div>
      {!!importing && <ImportDialog onClose={() => setImporting(false)} onAccept={onImport} />}
    </li>
  )
}

function LibraryCategory({
  showEmpty,
  category,
  root,
  collapsed,
  onToggle,
  noCreate,
  ...props
}: {
  showEmpty?: boolean
  category: CustomNode
  root?: boolean
  collapsed?: Record<string, boolean>
  onToggle: (name: string) => void
  noCreate?: boolean
  filter?: ItemFilter
  active: number | undefined
  setActive: (id: number | undefined) => void
}) {
  return (
    <li
      className={classNames({
        'poe-expanded': root || !collapsed || !collapsed?.[category.name]
      })}>
      {!root && (
        <div className="poe-category-header" onClick={collapsed ? () => onToggle(category.name) : undefined}>
          <span>{category.name}</span>
          {!!collapsed && <FontAwesomeIcon icon={faChevronDown} className="poe-category-toggle" />}
        </div>
      )}
      {!!(root || category.children) && (
        <ul className="poe-category-list">
          {!!showEmpty && <LibraryEmpty {...props} />}
          {!!root && !noCreate && <LibraryCreate {...props} />}
          {!!root && !noCreate && <LibraryImport {...props} />}
          {!!category.children &&
            category.children.map((item) => (
              <LibraryCategory key={item.name} category={item} collapsed={collapsed} onToggle={onToggle} {...props} />
            ))}
        </ul>
      )}
      {!!category.items && (
        <ul className="poe-item-list">
          {category.items.map((id) => (
            <LibraryItem key={id} id={id} {...props} />
          ))}
        </ul>
      )}
    </li>
  )
}

const lineTypes = {
  enchant: 'Highlight Enchants',
  implicit: 'Highlight Implicits',
  fractured: 'Highlight Fractured',
  explicit: 'Highlight Explicits',
  crafted: 'Highlight Crafted'
}

function TooltipLink({ item, id, onClose }: { item: Item; id: number; onClose: () => void }) {
  const [type, setType] = React.useState('link')
  const [label, setLabel] = React.useState(false)
  const [masks, setMasks] = React.useState<ItemTooltipOptions>({})
  const lines = React.useMemo(() => {
    const result: {
      type: StatType
      lines: string[]
    }[] = []
    function addStats(type: StatType, stats: Record<string, number>) {
      if (!stats) return
      const lines = Trans[itemTranslationDomain(Data.items[item.base])].formatStats(stats)
      if (!lines.length) return
      result.push({ type, lines })
    }
    if (!item.stats) return result
    addStats('enchant', item.stats.enchant)
    addStats('implicit', item.stats.implicit)
    addStats('fractured', item.stats.fractured)
    addStats('explicit', statsExtend({}, item.stats.explicit, item.stats.custom, item.stats.unique))
    addStats('crafted', item.stats.crafted)
    return result
  }, [item])
  const onToggleArt = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => setMasks((m) => ({ ...m, art: e.target.checked })),
    []
  )
  const onToggleLine = (type: StatType, index: number, value: boolean) =>
    setMasks((m) => {
      let t = m[type] || 0
      if (value) t |= 1 << index
      else t &= ~(1 << index)
      return { ...m, [type]: t }
    })

  const overlays = React.useContext(OverlayContext)
  const storeid = useImportId()
  const onCopy = React.useCallback(
    (e: React.MouseEvent) => {
      const args = [`id=${id}`, `profile=${storeid}`]
      if (Object.keys(masks).length) {
        args.push(`options=${encodeTooltipOptions(masks)}`)
      }
      if (type === 'preview') {
        args.push(`preview=${label ? 1 : 0}`)
      } else if (type === 'tooltip') {
        args.push(`tooltip`)
      }
      copyText(`[poe-item ${args.join(' ')}]${itemGetName(item)}[/poe-item]`)
      overlays.async(<CopyNotify pos={{ left: e.pageX, top: e.pageY }} />)
      onClose()
    },
    [id, masks, storeid, overlays, item, onClose, label, type]
  )

  return (
    <Dialog className="poe-TooltipLink" modal title="Tooltip Embed" onClose={onClose}>
      <div className="poe-TooltipLink-content">
        <select value={type} onChange={(e) => setType(e.target.value)}>
          <option value="link">Link</option>
          <option value="preview">Image</option>
          <option value="tooltip">Tooltip</option>
        </select>
        {type === 'preview' && (
          <label>
            <input type="checkbox" checked={label} onChange={(e) => setLabel(!!e.target.checked)} /> Show Label
          </label>
        )}
        <label>
          <input type="checkbox" checked={masks.art} onChange={onToggleArt} /> Show Artwork
        </label>
        {lines.map(({ type, lines }) => (
          <React.Fragment key={type}>
            <h3>{lineTypes[type]}</h3>
            {lines.map((line, index) => (
              <label key={index}>
                <input
                  type="checkbox"
                  checked={((masks[type] || 0) & (1 << index)) !== 0}
                  onChange={(e) => onToggleLine(type, index, e.target.checked)}
                />
                {line}
              </label>
            ))}
          </React.Fragment>
        ))}
      </div>
      <Dialog.Footer>
        <button onClick={onCopy}>Copy</button>
      </Dialog.Footer>
    </Dialog>
  )
}

export function ItemHeader({ item, id, children }: { item: Item; id: number; children?: ReactNode }) {
  const [tooltip, setTooltip] = React.useState(false)
  const isCC = Session.useSelector((state) => (state.user?.active ?? 0) >= EmbedLevel)
  const storeid = useImportId()
  const overlays = React.useContext(OverlayContext)
  const base = (item.unique && Data.uniques[item.unique]) || Data.items[item.base]
  const onCopyLink = React.useCallback(
    (e: React.MouseEvent) => {
      const args = [`id=${id}`, `profile=${storeid}`]
      copyText(`[poe-item ${args.join(' ')}]${itemGetName(item)}[/poe-item]`)
      overlays.async(<CopyNotify pos={{ left: e.pageX, top: e.pageY }} />)
    },
    [overlays, id, storeid, item]
  )

  const onExport = React.useCallback(
    (e: React.MouseEvent) => {
      copyText(JSON.stringify(item))
      overlays.async(<CopyNotify pos={{ left: e.pageX, top: e.pageY }} />)
    },
    [overlays, item]
  )

  if (!base) return null

  return (
    <div className="poe-ItemHeader">
      <span className={`poe-name poe-item-${item.rarity}`}>{itemGetName(item)}</span>
      {!!(id && storeid && isCC) && (
        <>
          <Tooltip tooltip="Copy item link">
            <FontAwesomeIcon className="poe-copy" icon={faCopy} onClick={onCopyLink} />
          </Tooltip>
          <Tooltip tooltip="Copy advanced item link">
            <FontAwesomeIcon className="poe-copy" icon={faCog} onClick={() => setTooltip(true)} />
          </Tooltip>
        </>
      )}
      <div className="poe-buttons">
        <button onClick={onExport}>Export</button>
        {children}
      </div>
      {tooltip && <TooltipLink item={item} id={id} onClose={() => setTooltip(false)} />}
    </div>
  )
}

function LibraryItemEditor({
  id,
  onSelect,
  noStats
}: {
  id: number
  onSelect: (id: number | undefined) => void
  noStats?: boolean
}) {
  const item = Store.useItem(id)
  const dispatch = Store.useDispatch()
  const onChange = React.useCallback(
    (value: Item | ((prev: Item) => Item)) =>
      dispatch.setItem(id, (item) => {
        if (!item) return item
        return typeof value === 'function' ? value(item) : value
      }),
    [dispatch, id]
  )
  const onDelete = React.useCallback(() => dispatch.setItem(id, undefined), [id, dispatch])
  const store = Store.useStore()
  const onClone = React.useCallback(() => {
    const items = store.getState().items
    let id = 1
    while (items[id]) id += 1
    dispatch.setItem(id, item)
    onSelect(id)
  }, [item, dispatch, store, onSelect])
  if (!item) return null
  return (
    <ItemEditor
      item={item}
      onChange={onChange}
      header={
        <ItemHeader item={item} id={id}>
          <button onClick={onClone}>Clone</button>
          <button onClick={onDelete}>Delete</button>
        </ItemHeader>
      }
      preview={<ItemDisplay item={item} options={undefined} label={undefined} />}
      noStats={noStats}
    />
  )
}

export function CustomItemList({
  filter,
  selected,
  onSelect,
  showEmpty,
  noCreate
}: {
  filter?: ItemFilter
  selected?: number
  onSelect: (id: number | undefined) => void
  showEmpty?: boolean
  noCreate?: boolean
}) {
  const [search, setSearch] = React.useState('')
  const onSetSearch = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value), [])
  const onClearSearch = React.useCallback(() => setSearch(''), [])

  const searchRe = React.useMemo(() => search.trim() && searchRegex(search.trim(), true), [search])

  const items = Store.useSelector((state) => state.items)
  const filtered = React.useMemo(
    () =>
      searchRe || filter
        ? Object.fromEntries(
            Object.entries(items).filter(([id, item]) => {
              if (!item) return false
              if (searchRe && !itemGetName(item!).match(searchRe)) return false
              if (filter && !filter.filter(item!)) return false
              return true
            })
          )
        : items,
    [items, filter, searchRe]
  )
  const root = React.useMemo(() => libraryCategories(filtered), [filtered])

  const [collapsed, setCollapsed] = React.useState<Record<string, boolean>>({})
  const onToggle = React.useCallback((id: string) => setCollapsed((exp) => ({ ...exp, [id]: !exp[id] })), [])

  return (
    <>
      <div className="poe-library-filters">
        <div className="poe-library-search">
          <input placeholder="Search..." value={search} onChange={onSetSearch} />
          {!!search.trim() && <FontAwesomeIcon icon={faTimesCircle} className="poe-clear" onClick={onClearSearch} />}
        </div>
      </div>
      <ul>
        <LibraryCategory
          filter={filter}
          showEmpty={showEmpty}
          category={root}
          root={true}
          collapsed={searchRe ? undefined : collapsed}
          onToggle={onToggle}
          active={selected}
          setActive={onSelect}
          noCreate={noCreate}
        />
      </ul>
    </>
  )
}

export function CustomItemLibrary({
  filter,
  selected,
  onSelect,
  showEmpty,
  noStats
}: {
  filter?: ItemFilter
  selected: number | undefined
  onSelect: (id: number | undefined) => void
  showEmpty?: boolean
  noStats?: boolean
}) {
  return (
    <div className="poe-ItemLibrary poe-UniqueLibrary">
      <Resizable className="poe-ItemCategories" side="e">
        <CustomItemList filter={filter} selected={selected} onSelect={onSelect} showEmpty={showEmpty} />
      </Resizable>
      <div className="poe-content">
        {!!selected && <LibraryItemEditor id={selected} onSelect={onSelect} noStats={noStats} />}
      </div>
    </div>
  )
}

export default function CustomItemLibraryWrap() {
  const [selected, onSelect] = React.useState<number>()
  return <CustomItemLibrary selected={selected} onSelect={onSelect} />
}
